"use client";
import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { c } from "@/lib/vster/theme";
import Icon from "@/components/vster/Icon";
import { Markdown } from "./Markdown";
import { encodePlusInQuery } from "@/lib/attachHref";
import { FileViewer } from "@/components/vster/FileViewer";
import ProgramChatbot from "./ProgramChatbot";

// 부스터(Vster) 상세 탭 — 5탭(요약 / 공고문 / 제출서류 / 첨부파일 / AI 질문).
//   비주얼만 디자인 이식, 데이터는 전부 실 공고문 필드(getProgramDetail) 사용.
const mono = "'JetBrains Mono',monospace";

// 선두 '|'·구분행(---) 없이 파이프로 열을 나눈 텍스트인지 판별
//   (예: 제출서류 "1 | 사업계획서(필수) | ㅇ 온라인 업로드 | - "). 정식 마크다운 표는 제외.
function isPipeTable(text: string): boolean {
  const lines = text.trim().split(/\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return false;
  if (lines.some((l) => /^\|?\s*:?-{2,}/.test(l.replace(/[|\s]/g, "-")))) return false; // 정식표(구분행) → Markdown 에 위임
  const piped = lines.filter((l) => (l.match(/\|/g) ?? []).length >= 2).length;
  return piped / lines.length >= 0.6;
}

// 정식 마크다운 표(구분행 동반) 여부 — 선두 '|' 없이 'No. | … / ---|---|---' 형태도 인식.
//   (Markdown 컴포넌트와 동일 규칙: '|' 있는 줄 다음이 구분행이면 표) → Markdown 에 위임해 렌더.
function hasMdTable(text: string): boolean {
  const lines = text.split(/\n/);
  return lines.some((l, i) => l.includes("|") && i + 1 < lines.length && /^\|?[\s:|-]*-{2,}[\s:|-]*\|?$/.test(lines[i + 1].trim()));
}

// 제출서류가 '①②③ 본항목 + 들여쓰기 없는 - 주석' 형태의 줄구조 평문인지 판별.
//   이런 내용을 Markdown 으로 렌더하면 목록 정규식이 동그라미 숫자(①)를 마커로 떼어내 번호가 사라지고,
//   들여쓰기가 없어 '-' 주석을 부모 항목 아래 중첩 못 해 형제로 나열되며, [필수]/[선택] 평문이 목록을 끊는다.
//   → 원문 줄바꿈이 곧 정렬이므로 pre-wrap 으로 렌더하는 게 충실하다.
function isCircledPlainDoc(text: string): boolean {
  if (hasMdTable(text)) return false; // 정식 표는 Markdown 에 위임
  if (/^[ \t]+[-*•]\s+/m.test(text)) return false; // 들여쓴(중첩) 불릿 = 진짜 마크다운 목록 → Markdown
  return /^\s*[①-⑳]\s/m.test(text); // 동그라미 숫자 본항목이 있으면 줄구조 평문
}

// 헤더 없는 파이프 데이터를 표로 렌더(모든 행=본문). 후행 빈 셀은 정리.
function PipeTable({ text }: { text: string }) {
  const rows = text.trim().split(/\n/).map((l) => l.trim()).filter((l) => l.includes("|"))
    .map((line) => {
      const cells = line.split("|").map((x) => x.trim());
      while (cells.length > 1 && cells[cells.length - 1] === "") cells.pop();
      return cells;
    });
  const cols = Math.max(...rows.map((r) => r.length));
  return (
    <div style={{ overflowX: "auto", margin: "4px 0" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {Array.from({ length: cols }).map((_, ci) => (
                <td key={ci} style={{ border: "1px solid #e4e8f6", padding: "6px 10px", verticalAlign: "top", color: c.ink, lineHeight: 1.6 }}>{r[ci] ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 탭 순서와 1:1 대응하는 URL 키 (?tab=). 새로고침·공유 시 현재 탭 유지용.
const TAB_KEYS = ["summary", "notice", "docs", "files", "chat"] as const;
const TABS: { key: string; label: string }[] = [
  { key: "summary", label: "요약" },
  { key: "notice", label: "공고문" }, // 요약 바로 오른쪽으로 이동
  { key: "docs", label: "제출서류" },
  { key: "files", label: "첨부파일" },
  { key: "chat", label: "AI 질문" },
];

export interface ProgramTabsProps {
  docId: number;                   // 챗봇(ADK) 세션용
  title: string;                   // 챗봇 헤더 표시용
  summaryMd?: string | null;       // LLM markdown 요약 → '요약' 탭 상단 아코디언
  businessOverview?: string | null;
  summary?: string | null;
  supportField?: string | null;
  supportContent?: string | null;
  supportScale?: string | null;
  supportAmount?: string | null;
  targetAudience?: string | null;
  qualification?: string | null;
  excludedTarget?: string | null;
  applyMethod?: string | null;     // 신청/접수방법
  selectionMethod?: string | null; // 선정방법/절차
  submitDocs?: string | null;      // 제출서류
  docType?: "support" | "event" | null; // 공고문 타입 — 행사면 제출서류 탭 숨김·라벨 전환
  cat1?: string | null;            // 1차 분류 — '공모전·경진'이면 공모전 전용 라벨/섹션
  eventVenue?: string | null;      // 행사 장소(행사 doc 전용)
  eventFee?: string | null;        // 참가비(행사 doc 전용)
  eventCapacity?: string | null;   // 모집인원·정원(행사 doc 전용)
  attachments?: { name: string; url: string }[]; // 수집된 글의 첨부파일
  period: string;                  // 접수기간(표시용)
  contact?: string | null;         // 문의처(담당부서·담당자·전화·이메일)
  supportPeriod?: string | null;   // 사업기간
  region?: string;                 // 지원지역(신청대상지역, 원문 텍스트)
  supportArea?: string | null;     // 지원대상 광역시도(정규화)
  org?: string;                    // 주관기관
  applyUrl?: string | null;
  sourceUrl?: string | null;
  sourceFileId?: number | null;
  fileType?: string | null;   // 공고문 뷰어 분기(pdf/hwp/hwpx)
  pdfPath?: string | null;    // HWP→PDF 변환본 경로
  origName?: string | null;   // 원본 파일명
  isContentRenderedFile?: boolean; // 공고파일이 '본문 자동 렌더본(_content.pdf)' 인지 — true 면 공고문 탭은 PDF 대신 원본 content 표시
  content?: string | null;    // 수집 본문(HTML) — 공고파일 없을 때 폴백
}


export default function ProgramTabs(p: ProgramTabsProps) {
  const sp = useSearchParams();
  // 행사(event) doc 은 제출서류가 없으므로 '제출서류' 탭을 숨긴다.
  const isEvent = p.docType === "event";
  const isContest = isEvent && p.cat1 === "공모전·경진"; // 공모전·경진 — 요약 섹션/라벨 공모전용
  // URL(?tab=)에서 초기 탭 복원 (없거나 미매칭이면 0=요약). 행사인데 ?tab=docs 면 요약(0)으로.
  const initIdx = (() => {
    const i = Math.max(0, (TAB_KEYS as readonly string[]).indexOf(sp.get("tab") ?? ""));
    return isEvent && TAB_KEYS[i] === "docs" ? 0 : i;
  })();
  const [active, setActive] = useState(initIdx);
  // 한 번이라도 연 탭은 마운트를 유지(display 로만 숨김) → 다른 탭 갔다 와도 내용 보존
  //   (공고문 뷰어 스크롤·로드 상태, AI질문 챗봇 세션 등). 처음 열 때만 마운트(lazy).
  const [visited, setVisited] = useState<Set<number>>(() => new Set([initIdx]));
  // 탭 전환: 로컬 상태 즉시 반영 + URL 갱신(history.replaceState, 서버 재요청/스크롤 없음) → 새로고침해도 유지.
  const selectTab = (i: number) => {
    setActive(i);
    setVisited((s) => (s.has(i) ? s : new Set(s).add(i)));
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", TAB_KEYS[i] ?? String(i));
      window.history.replaceState(null, "", u.toString());
    } catch { /* SSR/비지원 환경 무시 */ }
  };

  const activeKey = TABS[active].key;
  const idxOf = (key: string) => (TAB_KEYS as readonly string[]).indexOf(key);
  // 무거운 인터랙티브 패널(공고문 뷰어 iframe·AI 챗봇)은 처음 열 때만 마운트(lazy) — 크롤 가치 없고 성능 부담.
  const LAZY_TABS = new Set(["notice", "chat"]);
  // 패널 래퍼 — 활성이면 표시, 아니면 display:none.
  //   텍스트 탭(요약·제출서류·첨부)은 '항상' 마운트해 초기 HTML(SSR)에 포함 → 검색엔진이 탭을 클릭하지
  //   않아도 지원내용·대상·제출서류 전문을 크롤한다(크롤링됨-색인안됨 대응). display:none 콘텐츠도 색인됨.
  //   무거운 탭만 visited(lazy) 유지.
  const panel = (key: string, node: React.ReactNode): React.ReactNode => {
    const shown = activeKey === key;
    if (LAZY_TABS.has(key)) {
      return visited.has(idxOf(key)) ? <div style={{ display: shown ? "block" : "none" }}>{node}</div> : null;
    }
    return <div style={{ display: shown ? "block" : "none" }}>{node}</div>;
  };
  const attachments = (p.attachments ?? []).filter((a) => a && a.name && a.url);
  // 포스터/웹자보 등 이미지 첨부 — 공고파일·본문이 없는 포스터형 글(주로 행사)의 공고문 탭에 이미지로 노출.
  const posterImages = attachments.filter((a) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(a.url) || /\.(png|jpe?g|gif|webp)$/i.test(a.name) || /포스터|웹자보|홍보물|안내문/.test(a.name));
  // 공고문 탭에서 PDF 뷰어를 쓸지: 공고파일이 있고, 그게 '본문 렌더본(_content.pdf)+원본 content 보유' 가 아닐 때.
  //   본문 렌더본(_content.pdf)은 본문 이미지만 PDF 로 합성해 그 아래 텍스트를 잃으므로, 원본 content(HTML:
  //   포스터 이미지 + 텍스트)를 대신 보여준다. (gipa op219 등 — 포스터 밑 일시·장소·신청링크 텍스트 유실 방지)
  const showFileViewer = !!p.sourceFileId && !(p.isContentRenderedFile && !!p.content);
  // 공고파일·본문이 없는 포스터형 글은 아래 공고문(행사 안내) 탭에 포스터를 '이미지'로 노출한다 → 그 포스터는 첨부파일 탭에서 중복 제거.
  const posterShownInNotice = !p.sourceFileId && !p.content && posterImages.length > 0;
  const posterUrls = posterShownInNotice ? new Set(posterImages.map((a) => a.url)) : new Set<string>();
  const fileAttachments = posterShownInNotice ? attachments.filter((a) => !posterUrls.has(a.url)) : attachments;
  const origin = p.sourceUrl ?? (p.sourceFileId ? `/api/support-files/${p.sourceFileId}/raw` : null);

  const linkBtn = (href: string, label: string): React.ReactNode => (
    <a href={href} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12, background: c.action, color: "#fff", padding: "10px 18px", borderRadius: 10, fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
      {label}<Icon name="arrow_outward" size={16} color="#fff" />
    </a>
  );

  return (
    <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 16px -14px rgba(14,23,38,.16)" }}>
      {/* 탭 바 */}
      <div style={{ display: "flex", flexWrap: "wrap", borderBottom: `1px solid ${c.line}`, padding: "0 6px" }}>
        {TABS.map((t, i) => {
          if (isEvent && t.key === "docs") return null; // 행사: 제출서류 탭 숨김(인덱스는 유지)
          const on = i === active;
          return (
            <button
              key={t.key}
              onClick={() => selectTab(i)}
              style={{
                flex: "none", fontFamily: "inherit", fontSize: 13, fontWeight: on ? 700 : 500,
                padding: "13px 11px", background: "none", border: "none",
                borderBottom: `2.5px solid ${on ? c.action : "transparent"}`,
                color: on ? c.actionDark : c.muted, cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {/* 행사 탭 'notice' 라벨: 공고파일(sourceFile)이 있으면 정식 '공고문', 없으면(포스터/본문) '행사 안내' */}
              {isEvent && t.key === "notice" && !p.sourceFileId ? (isContest ? "공모 안내" : "행사 안내") : t.label}
            </button>
          );
        })}
      </div>

      {/* 탭 내용 */}
      {/* 공고문 파일뷰어 탭은 패딩을 줄여 문서를 넓게 — 큰 좌우 여백이 뷰어를 좁게 만들던 것 개선. 그 외 탭은 기본 여백. */}
      <div style={{ padding: activeKey === "notice" && showFileViewer ? "12px 4px" : "26px 28px" }}>
        {/* 요약 탭: LLM 요약(summaryMd) 우선. 없으면(구 발췌 doc) 구조화 필드로 폴백 요약. */}
        {panel("summary", (<>
          {p.summaryMd && p.summaryMd.trim() ? (
          <SummaryAccordion md={stripSubmitDocs(p.summaryMd)} region={p.region ?? null} supportArea={p.supportArea ?? null} isEvent={isEvent} isContest={isContest} supportContent={p.supportContent ?? null} />
        ) : (() => {
          const rows: [string, string | null | undefined][] = p.docType === "event"
            ? [
                ["행사 개요", p.businessOverview],
                ["참가 대상", p.targetAudience],
                ["행사 프로그램", p.supportContent],
                ["장소", p.eventVenue],
                ["참가비", p.eventFee],
                ["모집인원", p.eventCapacity],
                ["참가 자격", p.qualification],
              ]
            : [
                ["사업개요", p.businessOverview],
                ["지원분야", p.supportField],
                ["지원대상", p.targetAudience],
                ["지원내용", p.supportContent],
                ["지원규모", p.supportScale],
                ["신청자격", p.qualification],
              ];
          const shown = rows.filter(([, v]) => v && v.trim());
          if (!shown.length) return null;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {shown.map(([label, v]) => {
                const val = v!.trim();
                // 필드 단위 판정: 마크다운 표(| … | + 구분행)나 불릿 목록이면 Markdown 렌더, 그 외는 평문(pre-wrap).
                //   (예: '지원내용' supportContent 가 '| 지원내용 | 금액 |' 표인 구버전 doc — summaryMd 없어 폴백 시 원문 '|---|' 노출되던 것 수정)
                const md = hasMdTable(val) || /^[ \t]*[-*]\s+/m.test(val) || /^[ \t]*\|.*\|/m.test(val);
                return (
                  <div key={label}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: c.muted, marginBottom: 6 }}>{label}</div>
                    {md ? (
                      <div style={{ fontSize: 14.5, lineHeight: 1.8, color: c.ink, wordBreak: "break-word" }}><Markdown text={val} /></div>
                    ) : (
                      <div style={{ fontSize: 14.5, lineHeight: 1.8, color: c.ink, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{val}</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}</>))}

        {/* 제출서류 탭 — 행사(event) doc 은 제출서류가 없어 표시하지 않음.
             지원사업: 신규는 LLM 마크다운(중첩 불릿), 기존 평문은 pre-wrap. */}
        {!isEvent && panel("docs", (
          <div>
            {p.submitDocs && p.submitDocs.trim() ? (
              // ① 선두 '|'·구분행 없는 파이프 테이블('N | 서류명 | 방법 | 대상')이면 HTML 표로 렌더(원문 노출 방지)
              //   ② 마크다운(불릿 '- '/'*' 또는 정식 표 '| … |')이면 Markdown 렌더
              //   ③ 그 외 평문은 pre-wrap
              isPipeTable(p.submitDocs) ? (
                <PipeTable text={p.submitDocs} />
              ) : isCircledPlainDoc(p.submitDocs) ? (
                // ①②③ + 들여쓰기 없는 '-' 주석의 줄구조 평문 → 원문 줄바꿈 보존(Markdown 이 번호·계층 무너뜨림 방지)
                <pre style={{ fontFamily: "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14.5, lineHeight: 1.8, color: c.ink, margin: 0 }}>{p.submitDocs.trim()}</pre>
              ) : hasMdTable(p.submitDocs) || /^[ \t]*[-*]\s+/m.test(p.submitDocs) || /^[ \t]*\|.*\|/m.test(p.submitDocs) ? (
                <Markdown text={p.submitDocs} />
              ) : (
                <pre style={{ fontFamily: "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14.5, lineHeight: 1.8, color: c.ink, margin: 0 }}>{p.submitDocs.trim()}</pre>
              )
            ) : (
              <div style={{ background: c.actionSoft, border: `1px solid ${c.actionBorder}`, borderRadius: 12, padding: 20 }}>
                <p style={{ fontSize: 14.5, lineHeight: 1.8, color: c.ink, margin: 0 }}>
                  제출 서류·신청 양식은 <strong>원문 공고문</strong>에서 확인하세요. (사업계획서·사업자등록증·재무제표 등은 사업별로 상이)
                </p>
                {origin && linkBtn(origin, "원문 공고문 보기")}
              </div>
            )}
          </div>
        ))}

        {/* 첨부파일 탭: 수집된 글의 첨부파일(원본 다운로드) 목록 */}
        {panel("files", (
          <div>
            {fileAttachments.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {fileAttachments.map((a, i) => (
                  <a key={i} href={encodePlusInQuery(a.url)} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 11, border: `1px solid ${c.line}`, borderRadius: 10, padding: "13px 15px", textDecoration: "none" }}>
                    <Icon name="attach_file" size={19} color={c.action} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: c.ink, wordBreak: "break-all" }}>{a.name}</div>
                    </div>
                    <Icon name="download" size={19} color={c.action} />
                  </a>
                ))}
                {/* 첨부 다운로드 실패 시 안내 — 일부 기관은 세션·만료 등으로 직접 다운로드가 막힘 */}
                <p style={{ fontSize: 12.5, color: c.muted, margin: "4px 2px 0", lineHeight: 1.6, textAlign: "right" }}>
                  다운로드가 안 될 경우 {origin ? (
                    <a href={origin} target="_blank" rel="noreferrer" style={{ color: c.actionDark, fontWeight: 600, textDecoration: "underline" }}>출처 페이지</a>
                  ) : "출처 페이지"}를 방문하여 다운로드하세요.
                </p>
              </div>
            ) : (
              <p style={{ fontFamily: mono, fontSize: 13, color: c.muted, margin: 0 }}>
                첨부파일이 없습니다. 원문 공고문을 확인하세요.
              </p>
            )}
          </div>
        ))}

        {/* 공고문 탭: 공고파일 뷰어 / 없으면 수집 본문 / 둘 다 없으면 원문 링크 */}
        {panel("notice", (
          showFileViewer ? (
            // 높이는 FileViewer 가 '1페이지(A4 세로) 비율'로 스스로 산출(aspectRatio) → 한 화면에 1페이지.
            //   래퍼는 고정 높이를 두지 않는다(모바일은 '열기/다운로드' 카드 자연 높이).
            <div className="notice-viewer-wrap">
              <FileViewer fileId={p.sourceFileId!} fileType={p.fileType ?? null} pdfPath={p.pdfPath ?? null} origName={p.origName ?? null} />
            </div>
          ) : p.content ? (
            <div>
              <style>{`
                .collected-content img{max-width:100%!important;height:auto}
                .collected-content a{color:${c.actionDark}}
                /* 목록·제목 들여쓰기/간격 복원 — 전역 리셋(프리플라이트)으로 ul/li 들여쓰기가 사라지는 것 방지.
                   본문기반 공고문(코리아그랜드세일 등)의 중첩 목록이 계단식으로 들여쓰기 되게 한다. */
                .collected-content h2{font-size:17px;font-weight:800;color:${c.ink};margin:0 0 12px;line-height:1.4}
                .collected-content h3{font-size:15px;font-weight:700;color:${c.ink};margin:20px 0 8px;line-height:1.4}
                .collected-content h2:first-child,.collected-content h3:first-child{margin-top:0}
                .collected-content p{margin:0 0 12px}
                .collected-content ul,.collected-content ol{margin:0 0 12px;padding-left:22px}
                .collected-content ul{list-style:disc}
                .collected-content ol{list-style:decimal}
                .collected-content ul ul,.collected-content ul ol,.collected-content ol ul,.collected-content ol ol{margin:6px 0 6px}
                .collected-content li{margin:5px 0;line-height:1.75;padding-left:3px}
                .collected-content dl{display:grid;grid-template-columns:auto 1fr;gap:3px 14px;margin:8px 0}
                .collected-content dt{font-weight:600;color:${c.muted}}
                .collected-content dd{margin:0}
                /* 신청 포털(비즈오케이 등) 위젯: 신청/로그인/관심등록 버튼은 뷰어에서 동작 안 하고 노이즈라 숨김 */
                .collected-content .sbtn,.collected-content .btn_box,.collected-content a.btn{display:none!important}
                /* 본문(스크롤콘텐츠)에 박힌 축소·회색 인라인 스타일(font-size:12px 등) 무력화 → 뷰어 기본 가독성 복원 */
                .collected-content .scrollcontents,.collected-content .scrollcontents *{font-size:inherit!important;color:inherit!important;font-family:inherit!important;background:transparent!important}
                .collected-content .scrollcontents p{margin:0 0 8px;line-height:1.8}
                /* dl 섹션 라벨(사업개요·지원분야 등)은 위로 소제목처럼, 값은 아래 본문 블록으로 간격 확보 */
                .collected-content dl+dl{margin-top:14px}
                .collected-content dt{line-height:1.6}
                /* 수집 표(losims 등 .tableBox/table) — 전용 CSS 없이도 정리돼 보이게 기본 스타일 부여 */
                .collected-content h4{font-size:15px;font-weight:700;color:${c.ink};margin:20px 0 8px}
                .collected-content h4:first-child{margin-top:0}
                .collected-content .tableBox,.collected-content .h4_content{overflow-x:auto;margin:0 0 14px}
                .collected-content table{width:100%;max-width:100%;border-collapse:collapse;border-top:2px solid ${c.ink};font-size:13.5px}
                .collected-content caption{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
                .collected-content th,.collected-content td{border-bottom:1px solid ${c.line};padding:9px 12px;text-align:left;vertical-align:top;line-height:1.6}
                .collected-content th[scope=row],.collected-content thead th{background:${c.actionSoft};font-weight:600;color:${c.muted};white-space:nowrap}
              `}</style>
              <div style={{ fontFamily: mono, fontSize: 12, color: c.muted, marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${c.line}` }}>수집된 공고 내용</div>
              <div style={{ fontSize: 14.5, lineHeight: 1.8, color: c.ink, wordBreak: "break-word" }} className="collected-content" dangerouslySetInnerHTML={{ __html: p.content }} />
            </div>
          ) : posterImages.length > 0 ? (
            // 공고파일·본문이 없고 포스터 이미지만 있는 글(주로 행사) — 포스터를 공고문으로 노출.
            <div>
              <div style={{ fontFamily: mono, fontSize: 12, color: c.muted, marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${c.line}` }}>공고 포스터</div>
              {posterImages.map((a, i) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img key={i} src={`/api/img-proxy?url=${encodeURIComponent(a.url)}`} alt={a.name} style={{ width: "100%", height: "auto", borderRadius: 10, marginBottom: 12, border: `1px solid ${c.line}` }} />
              ))}
            </div>
          ) : (
            <div style={{ background: c.actionSoft, border: `1px solid ${c.actionBorder}`, borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 14.5, lineHeight: 1.8, color: c.ink, margin: 0 }}>공고문 파일·본문이 수집되지 않았습니다.</p>
              {origin && linkBtn(origin, "원문 공고문 보기")}
            </div>
          )
        ))}

        {/* AI 질문 탭: 기존 공고문 챗봇(ADK 백엔드)을 인라인 패널로 */}
        {panel("chat", (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: c.actionDark, marginBottom: 4 }}>
              <Icon name="forum" size={17} color={c.action} />공고문 챗봇
            </div>
            <div style={{ fontSize: 12.5, color: c.muted, marginBottom: 16 }}>공고 원문을 검색해 답변하며, 답변마다 근거 위치를 표시합니다</div>
            <div style={{ height: "min(620px, calc(100vh - 220px))", minHeight: 460 }}>
              <ProgramChatbot docId={p.docId} title={p.title} variant="inline" />
            </div>
          </>
        ))}
      </div>
    </div>
  );
}

// LLM 요약(markdown) 을 '## ' 헤딩 단위로 나눠 번호 매긴 접이식 카드(아코디언)로 표시.
// 행사 doc 요약은 저장값이 지원사업 프레임(사업 개요/지원 대상/지원 내용/평가·선정)으로 들어있다.
//   재추출 없이 '표시 단계'에서 섹션 제목만 행사 맥락으로 치환하고, 행사에 무의미한 '평가 및 선정 절차'는 숨긴다.
//   (내용 본문은 그대로 — 제목만 매핑)
const EVENT_SECTION_TITLE: Record<string, string> = {
  "사업 개요 및 배경": "행사 개요",
  "지원 대상 및 자격 요건": "참가 대상",
  "지원 내용 및 규모": "프로그램",
};
// 공모전·경진 전용 섹션명 — 공모전은 '심사·선정'이 핵심이라(행사와 달리) 선정 절차를 숨기지 않는다.
const CONTEST_SECTION_TITLE: Record<string, string> = {
  "사업 개요 및 배경": "공모 개요",
  "지원 대상 및 자격 요건": "응모 자격",
  "지원 내용 및 규모": "공모 분야 / 시상",
  "평가 및 선정 절차": "심사 / 선정",
  "신청 방법": "응모 방법",
};
function SummaryAccordion({ md, region, supportArea, isEvent, isContest, supportContent }: { md: string; region?: string | null; supportArea?: string | null; isEvent?: boolean; isContest?: boolean; supportContent?: string | null }) {
  const parsed = parseSummarySections(md);
  // 행사 프로그램 표: supportContent 가 표/파이프 형태면 요약의 '프로그램' 섹션 본문을 이 원문 표로 대체(요약 불릿보다 정확).
  const scTable = supportContent?.trim() && (hasMdTable(supportContent) || /^[ \t]*\|.*\|/m.test(supportContent)) ? supportContent.trim() : null;
  const sections = isContest
    ? parsed.map((s) => {
        // 공모전: 선정 절차(심사)를 숨기지 않고 '심사/선정'으로 보존. 섹션명만 공모전용으로 변환.
        const title = CONTEST_SECTION_TITLE[s.title.trim()] ?? s.title;
        const body = (scTable && s.title.trim() === "지원 내용 및 규모") ? scTable : s.body;
        return { ...s, title, body };
      })
    : isEvent
    ? parsed
        .filter((s) => !/평가\s*및\s*선정|선정\s*절차/.test(s.title)) // 행사엔 선정 절차 없음 → 숨김
        .map((s) => {
          const title = EVENT_SECTION_TITLE[s.title.trim()] ?? s.title;
          // '지원 내용 및 규모'(→프로그램) 섹션은 supportContent 표가 있으면 그 표로 렌더.
          const body = (scTable && (s.title.trim() === "지원 내용 및 규모" || title === "프로그램")) ? scTable : s.body;
          return { ...s, title, body };
        })
    : parsed;
  const [open, setOpen] = useState<boolean[]>(() => sections.map(() => true));
  if (sections.length === 0) return <Markdown text={md} />;
  // '지원 대상 및 자격 요건' 섹션 제목 우측에 지원대상지역 원시데이터 표시 — support_area → region 순. 동일값은 1회.
  const areaLabel = [...new Set([supportArea, region].filter((x): x is string => !!x))].join(" · ");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {sections.map((s, i) => {
        const isOpen = open[i];
        return (
          <div key={i} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setOpen((o) => o.map((v, j) => (j === i ? !v : v)))}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 16px",
                background: isOpen ? c.surfaceAlt : c.surface, border: "none", cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 6, background: c.action, color: "#fff", fontFamily: mono, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
              {/* 섹션 제목 = 시맨틱 H2 (아코디언 버튼 안이라도) — 검색·AI 답변엔진 구조화용. margin 리셋해 레이아웃 유지. */}
              <h2 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 700, color: c.ink, lineHeight: 1.4 }}>{s.title}</h2>
              {/* 지원대상지역(섹션 제목 우측) — 공모전(응모 자격)엔 지역 요건이 안 맞아 숨김. 지원사업/행사는 유지. */}
              {areaLabel && !isContest && /지원\s*대상|자격/.test(s.title) && (
                <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 3, color: c.muted, fontSize: 12.5, fontWeight: 600 }} title="지원대상지역">
                  <Icon name="place" size={14} />{areaLabel}
                </span>
              )}
              <span style={{ flexShrink: 0, color: c.muted, fontSize: 13, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▶</span>
            </button>
            {isOpen && (
              <div style={{ padding: "4px 18px 18px", borderTop: `1px solid ${c.line}` }}>
                <Markdown text={s.body} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// 요약에서 '제출 서류' 항목 제거 — 전용 '제출서류' 탭과 중복 방지.
//   (신규 생성분은 프롬프트에서 이미 제외되지만, 기존 문서의 summaryMd 대응)
//   · 결합 헤딩 "## 신청 방법 및 제출 서류" → "## 신청 방법" 으로 리네임
//   · 순수 "## 제출 서류" 섹션은 다음 헤딩까지 통째 제거
//   · '- 제출 서류' 불릿(인라인/부모) + 하위 들여쓴 불릿 제거
function stripSubmitDocs(md: string): string {
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (/^#{1,4}\s/.test(trimmed) && /제출\s*서류/.test(trimmed)) {
      if (/신청/.test(trimmed)) {
        out.push(line.replace(/\s*(및|,|\/|·)?\s*제출\s*서류/, ""));
        continue;
      }
      let j = i + 1;
      while (j < lines.length && !/^#{1,4}\s/.test(lines[j].trim())) j++;
      i = j - 1;
      continue;
    }
    const m = line.match(/^(\s*)[-*]\s+\*{0,2}\s*제출\s*서류/);
    if (m) {
      const indent = m[1].length;
      let j = i + 1;
      while (j < lines.length) {
        const nx = lines[j];
        if (nx.trim() === "") { j++; continue; }
        const ind = (nx.match(/^(\s*)/)?.[1] ?? "").length;
        if (ind > indent) { j++; continue; }
        break;
      }
      i = j - 1;
      continue;
    }
    out.push(line);
  }
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// '## 제목' 헤딩으로 섹션 분리. 헤딩 없으면 빈 배열(호출부에서 통짜 Markdown 폴백).
function parseSummarySections(md: string): { title: string; body: string }[] {
  const parts = md.split(/\n(?=##\s)/).map((s) => s.trim()).filter(Boolean);
  const out: { title: string; body: string }[] = [];
  for (const part of parts) {
    const m = part.match(/^##\s*(.+?)(?:\n([\s\S]*))?$/);
    if (!m) continue; // '## ' 로 시작 안 하는 조각(헤딩 없는 서두)은 무시
    const title = m[1].trim().replace(/^\d+[.)]\s*/, "");
    const body = (m[2] ?? "").trim();
    if (title) out.push({ title, body });
  }
  return out;
}
